import React, { useState, useEffect } from "react";
import Step0 from "./IndexRegistration/Step0";
import Step1 from "./IndexRegistration/Step1";
import Step2 from "./IndexRegistration/Step2";
import Step3 from "./IndexRegistration/Step3";
import styles from "./IndexRegistration.module.scss";
import SessionExpiredModal from "components/SessionExpiredModal";
import SessionTimer from "components/SessionTimer";
import { cookieHelpers } from "helpers/cookieHelpers";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { requestJson, RequestMethod } from "helpers/httpHelper";

const IndexRegistration = () => {
  const isDateInInterval = () => {
    const today = new Date();
    const dayOfMonth = today.getDate();

    const currentUrl = window.location.hostname;
    const isDevOrUat = currentUrl.includes('localhost') || currentUrl.includes('nova-dev.ro');

    if (isDevOrUat) {
      return dayOfMonth >= 1 && dayOfMonth <= 31;
    } else {
      return dayOfMonth >= 19 && dayOfMonth <= 26;
    }
  };

  const [currentStep, setCurrentStep] = useState(isDateInInterval() ? 1 : 0);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [isValidatingHash, setIsValidatingHash] = useState(false);
  const [stepsState, setAllStepsState] = useState({
    step1: {
      customer_no: "",
    },
    step2: {
      locations: [],
    },
  });

  const persistState = (funct) => {
    const prevState = stepsState;
    const data = funct(prevState);
    sessionStorage.setItem("indexRegistrationStepsState", JSON.stringify(data));
    setAllStepsState(funct);
  };

  useEffect(() => {
    const inValidInterval = isDateInInterval();

    if (!inValidInterval) {
      setCurrentStep(0);
      window.history.replaceState({}, document.title, window.location.pathname);
      sessionStorage.removeItem("indexRegistrationStepsState");
      sessionStorage.removeItem("indexRegistrationCurrentStep");
      return;
    }

    const validateHashAndLoadMeters = async (hash) => {
      setIsValidatingHash(true);
      try {
        const { response, isSuccess } = await requestJson(
          `account/validate-hash?h=${hash}`,
          RequestMethod.GET
        );

        if (!isSuccess) {
          // Check if it's a link expired error
          if (response?.error) {
            toast.error("Linkul a expirat. Vă rugăm să reintroduceți codul client.");
          } else {
            toast.error("Link invalid. Vă rugăm să reintroduceți codul client.");
          }
          // Clear hash from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          setIsValidatingHash(false);
          return;
        }

        if (!response?.success) {
          toast.error("Link invalid. Vă rugăm să reintroduceți codul client.");
          // Clear hash from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          setIsValidatingHash(false);
          return;
        }

        // Hash is valid, load meters data
        cookieHelpers.setSessionExpiryCookie();

        const metersData = response.response.meters.map((meter, index) => ({
          id: index + 1,
          metering_point_no: meter.metering_point_no,
          customer_no: meter.customer_no,
          customer_name: meter.customer_name,
          serie_contor: meter.meter_no_series,
          index_vechi: meter.current_index || "",
          index_nou: "",
          validated: false,
          utility: meter.utility,
          dial_code: meter.dial_code,
          unit_of_measure: meter.unit_of_measure || "kWh",
          address: meter.address,
        }));

        const newState = {
          step1: {
            customer_no: metersData[0]?.customer_no || "",
          },
          step2: {
            locations: metersData,
          },
        };

        setAllStepsState(newState);
        sessionStorage.setItem("indexRegistrationStepsState", JSON.stringify(newState));

        // Clear hash from URL
        window.history.replaceState({}, document.title, window.location.pathname);

        setCurrentStep(2);
        setIsValidatingHash(false);
      } catch (error) {
        toast.error("A apărut o eroare. Vă rugăm să încercați din nou.");
        setIsValidatingHash(false);
      }
    };

    // Check for hash in URL query params
    const urlParams = new URLSearchParams(window.location.search);
    const hash = urlParams.get("h");

    if (hash) {
      // Validate hash and load meters
      validateHashAndLoadMeters(hash);
    } else {
      // Normal flow - load from session storage
      const savedStepsState = JSON.parse(sessionStorage.getItem("indexRegistrationStepsState"));
      const savedStep = parseInt(sessionStorage.getItem("indexRegistrationCurrentStep"));

      if (sessionStorage.getItem("indexRegistrationCurrentStep") !== null) {
        setCurrentStep(savedStep);
      }
      if (savedStepsState) {
        setAllStepsState(savedStepsState);
      }
    }
  }, []);

  useEffect(() => {
    if (currentStep > 0) {
      sessionStorage.setItem("indexRegistrationCurrentStep", currentStep.toString());
    }
  }, [currentStep]);

  useEffect(() => {
    const checkSessionExpiration = () => {
      const expiryDate = cookieHelpers.getSessionExpiryCookie();

      if (expiryDate) {
        const remaining = expiryDate.getTime() - Date.now();
        if (remaining <= 0) {
          cookieHelpers.clearAllSessionData();
          setShowSessionExpiredModal(true);
          return;
        }
      }

      if (cookieHelpers.isSessionExpired()) {
        cookieHelpers.clearAllSessionData();
        setShowSessionExpiredModal(true);
      }
    };

    if (currentStep === 2) {
      checkSessionExpiration();
      const interval = setInterval(checkSessionExpiration, 1000);
      return () => clearInterval(interval);
    }
  }, [currentStep]);

  const handleSessionRestart = () => {
    cookieHelpers.clearAllSessionData();
    sessionStorage.removeItem("indexRegistrationStepsState");
    sessionStorage.removeItem("indexRegistrationCurrentStep");
    window.location.reload();
  };

  return (
    <>
      <section className={styles.headerSection}>
        <div className={styles.indexContainer}>
          <h1 className={styles.headerTitle}>
            Transmite indexul de consum energie electrică și gaze naturale
          </h1>
          {currentStep === 2 && <SessionTimer />}
        </div>
      </section>

      {isValidatingHash ? (
        <section className="pb-55">
          <div className={styles.indexContainer}>
            <div className="text-center" style={{ padding: "50px 0" }}>
              <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem", marginBottom: "20px" }}></div>
              <p style={{ margin: 0 }}>Se validează linkul...</p>
            </div>
          </div>
        </section>
      ) : (
        (() => {
          switch (currentStep) {
            case 0:
              return <Step0 />;
            case 1:
              return (
                <Step1
                  stepsState={stepsState.step1}
                  persistState={persistState}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                />
              );
            case 2:
              return (
                <Step2
                  stepsState={stepsState}
                  persistState={persistState}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                />
              );
            case 3:
              return <Step3 />;
            default:
              return (
                <Step1
                  stepsState={stepsState.step1}
                  persistState={persistState}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                />
              );
          }
        })()
      )}
      {showSessionExpiredModal && <SessionExpiredModal onRestart={handleSessionRestart} />}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
};

export default IndexRegistration;
